import React from 'react';
import classnames from 'classnames';
import type { HTMLAttributes } from 'react';
import type { AllColorType, AllIconType, SizeType } from '../../../util/types';
import { useI18n } from '@whizz/react-i18n';
import { InfoButton } from '../../../global';
import { getIconComponent } from '../../../util/reactFunc';

type HTMLProps = HTMLAttributes<HTMLDivElement>;
interface Props extends HTMLProps {
  /**
   * Sets icon along value/description.
   * Optional in order to add a text instead.
   */
  icon?: AllIconType;
  iconSize?: number | SizeType;
  iconColor?: AllColorType;
  /**
   * Sets title along value/description.
   * Optional in order to add an icon instead.
   */
  title?: string;
  /**
   * Prints more info button.
   */
  showMoreInfo?: boolean;
  /**
   * Handles click on more info button.
   */
  onMoreInfo?(): void;
  /**
   * Numeric value of the headline.
   */
  value: number | string | null | undefined;
  /**
   * Sets background color of value.
   */
  colorValue?: boolean;
  /**
   * Highlights value.
   */
  highlightValue?: boolean;
  /**
   * Unit after value.
   */
  unit?: string;
  /**
   * Description after value.
   */
  description?: string;
  /**
   * Show Colon after title
   */
  showColon?: boolean;
}

const ValueHeadline = ({ icon, iconSize, iconColor, title, showMoreInfo = true, onMoreInfo = () => console.info(''), value, highlightValue = false, colorValue = false, description, unit, showColon = true, ...other }: Props): JSX.Element => {
  const { translate } = useI18n();
  return (
    <div {...other as HTMLProps}>
      <div className={'md:flex flex-nowrap items-center'}>
        {(icon || title) && (
          <div className={'mr-sm mb-sm flex flex-nowrap items-center'}>
            {icon && getIconComponent(icon, iconSize, iconColor)}
            {title && <b className={`${classnames({ 'ml-sm': !!icon })}`}>{title}{showColon && translate('global.COLON')}</b>}
          </div>
        )}
        <div className={`flex items-center ${classnames({ 'mb-sm': ((icon || title)) })}`}>
          <div className={`flex items-center${classnames(
            {
              ' rounded-default py-1 px-2' : (colorValue || highlightValue),
              ' bg-header' : colorValue && !highlightValue,
              ' border-3 border-primary text-primary': highlightValue })}
          `}>
            {value !== null && (
              <div className={'text-xl md:text-2xl leading-none md:leading-tight font-bold'}>
                {value}
              </div>
            )}
            {unit && (
              <div className={`${classnames({ 'ml-sm': value !== null })} leading-none`}>
                {unit}
              </div>
            )}
          </div>
          {description && (
            <div className={'ml-sm leading-none'}>
              {description}
            </div>
          )}
          {showMoreInfo && (
            <InfoButton onClick={() => onMoreInfo()} />
          )}
        </div>
      </div>
    </div>);
};

ValueHeadline.displayName = 'ValueHeadline';

export default ValueHeadline;
